// LandingPage.jsx

import { loadStripe } from '@stripe/stripe-js';

import PaymentModalWrapper from './PaymentModal'; // from above
import './PaymentModal.css'; // your modal styling

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaChessQueen } from 'react-icons/fa';
import LegalModal from './woodpecker/LegalModal';
import PuzzleWorkspace, { PuzzlePanel } from './components/puzzles/PuzzleWorkspace';
import { Button } from './components/ui/button';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import './LandingPage.css';
import { termsOfServiceText, privacyPolicyText } from './LegalText';

// Load your Stripe public key
const stripePromise = process.env.REACT_APP_STRIPE_PUBLIC_KEY 
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY)
  : null;

// Add a check for Stripe availability
const isStripeAvailable = () => {
  return !!process.env.REACT_APP_STRIPE_PUBLIC_KEY;
};

function LandingPage() {
  const [username, setUsername] = useState('');
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState('free');
  const [freeMonth, setFreeMonth] = useState('');
  const [freeUsername, setFreeUsername] = useState('');
  const [searchParams] = useSearchParams();
  const [showTosModal, setShowTosModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [idToken, setIdToken] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const navigate = useNavigate();

  // We consider a user "paid" if subscriptionStatus is "active" (or "paid").
  // const isPaid = userInfo ? true : false;
  const isPaid = subscriptionStatus === 'active';

  // --- 1) On initial mount, try to restore user from localStorage ---
  useEffect(() => {
    const token = localStorage.getItem('google_id_token');
    if (token) {
      const decoded = jwtDecode(token);
      // Optional: Check if token is expired
      if (decoded.exp && decoded.exp * 1000 > Date.now()) {
        setUserInfo(decoded);
        checkUserSubscriptionStatus(token, decoded.email);
      } else {
        // Token is expired or invalid, remove it
        localStorage.removeItem('google_id_token');
      }
    }
  }, []);
  // Check subscription status on mount and when returning from payment
  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (userInfo?.email) {
        // Add small delay to allow Stripe webhook to process
        if (searchParams.get('payment_status') === 'success') {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
        await checkUserSubscriptionStatus(localStorage.getItem('google_id_token'), userInfo.email);
      }
    };

    checkPaymentStatus();
  }, [userInfo, searchParams]);

  // 1) Function to call your backend to see if user is paid
  const checkUserSubscriptionStatus = async (token, email) => {
    try {
      // Example request: 
      // Adjust the URL to your actual Lambda or API Gateway endpoint
      const response = await fetch(`https://hsp1rr5g7l.execute-api.us-east-1.amazonaws.com/${email}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-Authorization-Provider': 'google' },
      });
      if (!response.ok) {
        // If the endpoint returns an error, handle it
        console.error('Error checking subscription status:', response.status);
        return;
      }

      const data = await response.json();
      // Suppose the response JSON looks like: { subscriptionStatus: "active" }
      if (data.subscriptionStatus) {
        setSubscriptionStatus(data.subscriptionStatus);
      }
      
      // Set the freeMonth if available
      if (data.freeMonth) {
        setFreeMonth(data.freeMonth);
      }
      
      // Set the freeUsername if available
      if (data.username) {
        setFreeUsername(data.username);
      }
    } catch (err) {
      console.error('Error fetching subscription status:', err);
    }
  };

  const handleLoginSuccess = (credentialResponse) => {
    const token = credentialResponse.credential;
    const decoded = jwtDecode(token);
    setUserInfo(decoded);
    setIdToken(token);
    checkUserSubscriptionStatus(token, decoded.email);

    // Optionally store the token for future requests
    localStorage.setItem('google_id_token', token);
  };

  const handleLoginError = () => {
    alert('Google Login Failed. Please try again.');
  };

  // Called when user wants to open the embedded subscription form
  const handleOpenPaymentModal = () => {
    if (!userInfo) {
      alert('Please sign in with Google first.');
      return;
    }
    if (!isStripeAvailable()) {
      alert('Payment system is currently unavailable. Please try again later.');
      return;
    }
    setShowPaymentModal(true);
  };
  
  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    setSubscriptionStatus('active');
    alert('Payment success! You are now paid.');
  };
  
  const handleCloseModal = () => {
    setShowPaymentModal(false);
  };

  const handleSubmit = () => {
    if (!username.trim()) {
      alert('Please enter a valid Lichess username.');
      return;
    }

    // If one of year/month is provided but not both, show an error
    if ((selectedMonth && !selectedYear) || (selectedYear && !selectedMonth)) {
      alert('Please select both a year and a month, or leave both blank.');
      return;
    }

    // Navigate to /puzzle, passing username, year, and month in location state
    const state = {
      username,
      selectedYear,
      selectedMonth,
      email: userInfo?.email,
      isPaid,
    };
    navigate('/puzzle', { state });
  };

  // Build options for year and month dropdowns
  const yearOptions = ['', '2019', '2020', '2021', '2022', '2023', '2024', '2025'];
  const monthOptions = [
    { value: '', label: '' },
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  // Filter year and month options based on freeMonth if available
  let filteredYearOptions = freeMonth && subscriptionStatus === 'free' 
    ? [freeMonth.split('-')[0]] : yearOptions;
  
  let filteredMonthOptions = freeMonth && subscriptionStatus === 'free'
    ? monthOptions.filter(month => month.value === freeMonth.split('-')[1])
    : monthOptions;

  if (freeMonth && subscriptionStatus === 'free') {
    filteredYearOptions.unshift('');
    filteredMonthOptions.unshift({ value: '', label: '' });
  }

  const handleOpenTos = (e) => {
    e.preventDefault();
    setShowTosModal(true);
  };

  const handleCloseTos = () => {
    setShowTosModal(false);
  };

  const handleOpenPrivacy = (e) => {
    e.preventDefault();
    setShowPrivacyModal(true);
  };

  const handleClosePrivacy = () => {
    setShowPrivacyModal(false);
  };

  const handleCancelSubscription = async (e) => {
    e.preventDefault();
    
    if (!userInfo || !isPaid) {
      return;
    }
    
    if (window.confirm('Are you sure you want to cancel your unlimited puzzles subscription? This action cannot be undone.')) {
      try {
        const response = await fetch('https://o5s6iz75t2.execute-api.us-east-1.amazonaws.com/cancel-subscription', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
            'X-Authorization-Provider': 'google',
          },
          body: JSON.stringify({
            email: userInfo.email
          }),
        });
        
        if (response.ok) {
          alert('Your subscription has been cancelled successfully.');
          // Update the subscription status locally
          setSubscriptionStatus('free');
        } else {
          const errorData = await response.json();
          alert(`Failed to cancel subscription: ${errorData.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error cancelling subscription:', error);
        alert('An error occurred while trying to cancel your subscription. Please try again later.');
      }
    }
  };

  return (
    <PuzzleWorkspace title="Create puzzles from your games" subtitle="Study your mistakes and find the solutions." contentLabel="Custom puzzle setup" showBack={false}>
      <div className="grid w-full items-start gap-5 px-4 sm:px-0 lg:grid-cols-2">
        <PuzzlePanel title="Your account">
          {!userInfo ? (
            <>
              <p className="text-sm text-slate-600">Sign in to choose a month. You can also start with just a Lichess username.</p>
              <GoogleLogin onSuccess={handleLoginSuccess} onError={handleLoginError} />
            </>
          ) : (
            <div className="space-y-3">
              <p className="m-0 break-words text-sm text-slate-700">Welcome, {userInfo.name}!</p>
              {isPaid ? (
                <p className="m-0 flex items-center gap-2 text-sm font-semibold text-sky-700"><FaChessQueen aria-hidden="true" /> Unlimited puzzles</p>
              ) : (
                <Button variant="neutral" size="action" className="m-0 w-full border-solid sm:w-auto" onClick={handleOpenPaymentModal}>Unlock unlimited puzzles</Button>
              )}
            </div>
          )}
        </PuzzlePanel>

        <PuzzlePanel title="Choose your games">
          <form aria-label="Choose your games" onSubmit={event => { event.preventDefault(); handleSubmit(); }} className="space-y-5">
            <div>
              <label htmlFor="custom-username" className="mb-2 block text-sm font-semibold text-slate-700">Lichess username</label>
              <input id="custom-username" type="text" placeholder="Your Lichess username" autoComplete="username"
                className="min-h-12 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-base text-slate-800 shadow-sm focus:border-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-700/20 disabled:bg-slate-100 disabled:text-slate-500"
                value={(!isPaid && selectedYear === freeMonth.split('-')[0] && selectedMonth === freeMonth.split('-')[1]) ? freeUsername : username}
                required onChange={e => setUsername(e.target.value)}
                disabled={!isPaid && selectedYear === freeMonth.split('-')[0] && selectedMonth === freeMonth.split('-')[1]} />
            </div>
            <fieldset className="min-w-0">
              <legend className="mb-2 text-sm font-semibold text-slate-700">Time period (optional)</legend>
              {!userInfo && <p className="text-sm text-slate-600">Sign in to select a year and month.</p>}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="custom-year" className="mb-2 block text-sm text-slate-600">Year</label>
                  <select id="custom-year" value={selectedYear || ''} onChange={e => setSelectedYear(e.target.value || null)} disabled={!userInfo}
                    className="min-h-12 w-full rounded-xl border border-slate-300 bg-white px-3 text-base text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-700 disabled:bg-slate-100 disabled:text-slate-500">
                    {filteredYearOptions.map(year => <option key={year} value={year}>{year || 'Any year'}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="custom-month" className="mb-2 block text-sm text-slate-600">Month</label>
                  <select id="custom-month" value={selectedMonth || ''} onChange={e => setSelectedMonth(e.target.value || null)} disabled={!userInfo}
                    className="min-h-12 w-full rounded-xl border border-slate-300 bg-white px-3 text-base text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-700 disabled:bg-slate-100 disabled:text-slate-500">
                    {filteredMonthOptions.map(month => <option key={month.value} value={month.value}>{month.label || 'Any month'}</option>)}
                  </select>
                </div>
              </div>
              {freeMonth && subscriptionStatus === 'free' && <p className="mb-0 mt-3 text-sm text-slate-600">
                Your free month: {monthOptions.find(m => m.value === freeMonth.split('-')[1])?.label} {freeMonth.split('-')[0]}
              </p>}
            </fieldset>
            <Button type="submit" variant="brand" size="action" className="m-0 w-full">Generate puzzles</Button>
          </form>
        </PuzzlePanel>
      </div>

      {/* Payment Modal with Stripe Elements */}
      {showPaymentModal && (
        <PaymentModalWrapper
          idToken={idToken}
          userEmail={userInfo.email}
          stripePromise={stripePromise}
          onClose={handleCloseModal}
          onSuccess={handlePaymentSuccess}
        />
      )}

      {/* Terms of Service Modal */}
      <LegalModal isOpen={showTosModal} onClose={handleCloseTos} title={termsOfServiceText.title}
        content={termsOfServiceText.sections.map((section, index) => (
                <div key={index}>
                  <h3>{section.title}</h3>
                  <p>{section.content}</p>
                </div>
              ))} />

      {/* Privacy Policy Modal */}
      <LegalModal isOpen={showPrivacyModal} onClose={handleClosePrivacy} title={privacyPolicyText.title}
        content={privacyPolicyText.sections.map((section, index) => (
                <div key={index}>
                  <h3>{section.title}</h3>
                  <p>{section.content}</p>
                  {section.list && (
                    <ul>
                      {section.list.map((item, itemIndex) => (
                        <li key={itemIndex}>{item.text}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))} />

      {/* Footer with TOS and Privacy Policy links */}
      <footer className="mx-auto mt-8 max-w-xl px-4 text-center text-sm text-slate-600">
        <div className="flex flex-wrap justify-center gap-2">
        <Button variant="link" size="sm" className="m-0 h-11 border-0 bg-transparent px-2 text-xs font-normal text-slate-500 hover:bg-transparent hover:text-slate-700" onClick={handleOpenTos}>Terms of Service</Button>
        <Button variant="link" size="sm" className="m-0 h-11 border-0 bg-transparent px-2 text-xs font-normal text-slate-500 hover:bg-transparent hover:text-slate-700" onClick={handleOpenPrivacy}>Privacy Policy</Button>
        {isPaid && (
          <Button variant="neutral" size="action" onClick={handleCancelSubscription}>Cancel Unlimited Puzzles</Button>
        )}
        </div>
        <p className="mt-4">© 2025 Puzzlify. Operated by Mountainsoft LLC. All rights reserved.</p>
      </footer>
    </PuzzleWorkspace>
  );
}

export default LandingPage;
